import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const GA_ID = import.meta.env.VITE_GA_ID || '';
const GTM_ID = import.meta.env.VITE_GTM_ID || 'GTM-WRWHK586';
const PH_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY || '';
const PH_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || '';
const CONSENT_KEY = 'cookie-consent';

let posthogInstance = null;

export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'analytics' in parsed) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveConsent(prefs) {
  const data = {
    necessary: true,
    analytics: !!prefs.analytics,
    functional: !!prefs.functional,
    marketing: !!prefs.marketing,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(data));

  if (data.analytics) {
    initAnalyticsInternal();
    trackEvent('consent_granted', {
      analytics: 1,
      functional: data.functional ? 1 : 0,
      marketing: data.marketing ? 1 : 0,
    });
  } else {
    revokeAnalyticsConsent();
  }
}

function ensureConsentMode() {
  if (window.__gtagConsentModeInit) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };

  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  window.__gtagConsentModeInit = true;
}

export function initAnalytics() {
  ensureConsentMode();

  const consent = getConsent();
  if (consent && consent.analytics) {
    initAnalyticsInternal();
  }
}

function loadTagManager() {
  if (!GTM_ID) return;
  if (window.gtmLoaded) return;
  window.gtmLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  script.onerror = () => console.error('[analytics] Failed to load GTM script:', GTM_ID);
  document.head.appendChild(script);
}

function initAnalyticsInternal() {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
    });
  } else {
    console.error('[analytics] window.gtag is missing — consent cannot be granted.');
  }

  loadTagManager();

  const PH_UI_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_UI_HOST || 'https://eu.posthog.com';

  if (PH_KEY && !posthogInstance) {
    import('posthog-js').then(mod => {
      mod.default.init(PH_KEY, {
        api_host: PH_HOST || undefined,
        ui_host: PH_UI_HOST,
        persistence: 'localStorage+cookie',
        autocapture: false,
        defaults: '2026-05-30',
        person_profiles: 'identified_only',
      });
      posthogInstance = mod.default;
    }).catch(e => {
      console.error('[analytics] PostHog failed to load:', e);
    });
  }
}

export function revokeAnalyticsConsent() {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
    });
  }
  if (posthogInstance) {
    posthogInstance.opt_out_capturing();
  }
}

export function grantAnalyticsConsent() {
  saveConsent({ analytics: true, functional: true, marketing: true });
}

export function trackEvent(action, params = {}) {
  const consent = getConsent();
  if (!consent || !consent.analytics) return;
  if (window.gtag) {
    window.gtag('event', action, params);
  }
  if (PH_KEY && posthogInstance) {
    posthogInstance.capture(action, params);
  }
}

export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    if (GA_ID && window.gtag) {
      window.gtag('config', GA_ID, {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location]);
}
